import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Button,
  Image,
  Grid,
  Tooltip,
  useBreakpointValue,
  useOutsideClick,
  Spinner,
} from "@chakra-ui/react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import ButtonUpChevron from "../Images/Button-Up-Chevron-icon-Stande2.0.svg";
import ButtonDownChevron from "../Images/Bottom-Chevron-Icon-Stande2.0.svg";
import HomeStroke from "../Images/Home-Icon-Stroke-Stande2.0.svg";
import ArrowRight from "../Images/Arrow-Right-icon-Stande2.0.svg";
import DashboardIcon from "../Images/Dashboard-icon.svg";
import DashboardIconDark from "../Images/Dashboard-icon-dark-Stande2.0.svg";
import MyPapersIcon from "../Images/My-papers-icon-1.svg";
import MyPapersIconDark from "../Images/My-papers-icon-dark-stande2.0.svg";
import MySubjectsIcon from "../Images/My-subjects-icon.svg";
import MySubjectsIconDark from "../Images/MySubjects-Icon-Dark-Stande2.0.svg";
import SettingsIcon from "../Images/Settings-icon.svg";
import SettingsIconDark from "../Images/setting-dark-icon-stande.2.0.svg";
import ClassroomsIcon from "../Images/Classrooms-icon.svg";
import LogoutIcon from "../Images/Logout-icon.svg";
import LogoutIconDark from "../Images/logout-dark-icon-stande2.0.svg";
import { ChevronDoubleLeftIcon } from "@heroicons/react/24/outline";
import { BASE_API_URL } from "./config";
import { logout } from "./auth/logout";
import { a } from "./appColors";
import { formatExamBoard } from './examBoards';


const IconRow = ({ label, lightSrc, darkSrc, onClick, isCurrent, collapsed }) => {
  const [hover, setHover] = useState(false);
  // Keep the neutral icon on hover/active — only the label takes the brand accent.
  const iconSrc = lightSrc;

  return (
    <Tooltip label={label} placement="right" hasArrow isDisabled={!collapsed} openDelay={150}>
      <Button
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onFocus={() => setHover(true)}
        onBlur={() => setHover(false)}
        onClick={onClick}
        variant="ghost"
        justifyContent={collapsed ? "center" : "flex-start"}
        borderRadius="12px"
        px={collapsed ? 0 : "12px"}
        py="8px"
        w="full"
        leftIcon={collapsed ? undefined : <Image className="app-nav-icon" src={iconSrc} alt="" w="34px" h="34px" />}
        _hover={{ bg: a.hover }}
        _active={{ bg: a.hover }}
        _focusVisible={{ boxShadow: "0 0 0 3px rgba(255,71,22,0.28)" }}
      >
        {collapsed ? (
          <Image className="app-nav-icon" src={iconSrc} alt={label} w="36px" h="36px" />
        ) : (
          <Text
            color={hover || isCurrent ? a.accent : a.text2}
            fontSize="12px"
            fontWeight="300"
          >
            {label}
          </Text>
        )}
      </Button>
    </Tooltip>
  );
};

const HomeChildRow = ({ label, isActive, onClick }) => {
  const [hover, setHover] = useState(false);
  const active = hover || isActive;
  const color = active ? a.accent : a.text2;

  return (
    <HStack h="24px" spacing="2px" align="center">
      <Box
        w="7px"
        h="1px"
        bg={a.border}
        transform="scaleY(0.2)"
        transformOrigin="center"
      />
      <Button
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onFocus={() => setHover(true)}
        onBlur={() => setHover(false)}
        onClick={onClick}
        variant="ghost"
        display="flex"
        w="138px"
        px="11px"
        py="6px"
        alignItems="center"
        justifyContent="space-between"
        gap="10px"
        borderRadius="24px"
        bg={active ? a.hover : "transparent"}
        _hover={{ bg: a.hover }}
        minH="32px"
        _focusVisible={{ boxShadow: "0 0 0 3px rgba(255,71,22,0.28)" }}
      >
        <Text fontSize="11px" fontWeight="300" color={color}>
          {label}
        </Text>
        <Image
          src={ArrowRight}
          alt=""
          w="20px"
          h="20px"
          opacity={active ? 1 : 0}
          transition="opacity .15s ease"
        />
      </Button>
    </HStack>
  );
};

const Sidebar = ({ collapsed = false, onToggle }) => {
  const [homeOpen, setHomeOpen] = useState(true);
  const [boards, setBoards] = useState([]);
  const [boardQuals, setBoardQuals] = useState({});
  const [subjectsByQual, setSubjectsByQual] = useState({});
  const [panelBoardGuid, setPanelBoardGuid] = useState(null);
  const [openQualGuid, setOpenQualGuid] = useState(null);
  const [loadingBoards, setLoadingBoards] = useState(false);
  const [loadingQualsForBoard, setLoadingQualsForBoard] = useState({});
  const [loadingSubjects, setLoadingSubjects] = useState({});
  const [current, setCurrent] = useState("");
  const [overlayTop, setOverlayTop] = useState(80); // where the overlay starts
  const location = useLocation();
  const [activePath, setActivePath] = useState(location.pathname);
  const navigate = useNavigate();
  const sidebarW = useBreakpointValue({ base: "205px", md: "205px" });
  const sidebarRef = useRef(null);
  const overlayRef = useRef(null);

  
  useOutsideClick({
    ref: overlayRef,
    handler: () => {
      if (homeOpen && selectedBoard) {
        setPanelBoardGuid(null);
        setOpenQualGuid(null);
      }
    },
  });

  useEffect(() => {
    setLoadingBoards(true);
    axios
      .get(`${BASE_API_URL}/api/v1/exam-boards/`)
      .then((res) => {
        setBoards(res.data || []);
      })
      .catch((err) => {
        console.error("Failed to load boards", err);
      })
      .finally(() => setLoadingBoards(false));
  }, []);

  const selectedBoard = useMemo(
    () => boards.find((b) => b.guid === panelBoardGuid) || null,
    [boards, panelBoardGuid]
  );

  const handleBoardClick = async (boardGuid, event) => {
    setPanelBoardGuid(boardGuid);
    setOpenQualGuid(null);

    // position overlay next to the clicked row
    if (event && sidebarRef.current) {
      const itemRect = event.currentTarget.getBoundingClientRect();
      const containerRect = sidebarRef.current.getBoundingClientRect();
      const offset = itemRect.top - containerRect.top;
      setOverlayTop(offset);
    }

    if (!boardQuals[boardGuid]) {
      setLoadingQualsForBoard((prev) => ({ ...prev, [boardGuid]: true }));
      try {
        const res = await axios.get(
          `${BASE_API_URL}/api/v1/exam-qualifications/by-exam-board-guid/${boardGuid}`
        );
        const list = res.data || [];
        setBoardQuals((prev) => ({ ...prev, [boardGuid]: list }));
      } catch (err) {
        console.error("Failed to load qualifications", err);
        setBoardQuals((prev) => ({ ...prev, [boardGuid]: [] }));
      } finally {
        setLoadingQualsForBoard((prev) => ({
          ...prev,
          [boardGuid]: false,
        }));
      }
    }
  };

  const makeButtonProps = (path) => ({
    gap: 2,
    w:'100%',
    variant: 'ghost',
    pl: collapsed ? 0 : 4,
    justifyContent: collapsed ? 'center' : 'left',
    bg: activePath === path ? '#FFEEEA' : 'transparent',
    _hover: { bg: '#FFEEEA' },
    borderRadius: '8px'
  });

  const handleQualToggle = async (qual) => {
    if (openQualGuid === qual.guid) {
      setOpenQualGuid(null);
      return;
    }

    setOpenQualGuid(qual.guid);

    if (!subjectsByQual[qual.guid]) {
      setLoadingSubjects((prev) => ({ ...prev, [qual.guid]: true }));
      try {
        const res = await axios.get(
          `${BASE_API_URL}/api/v1/subjects/by-exam-qualification-guid/${qual.guid}`
        );
        const list = res.data || [];
        setSubjectsByQual((prev) => ({ ...prev, [qual.guid]: list }));
      } catch (err) {
        console.error("Failed to load subjects", err);
        setSubjectsByQual((prev) => ({ ...prev, [qual.guid]: [] }));
      } finally {
        setLoadingSubjects((prev) => ({
          ...prev,
          [qual.guid]: false,
        }));
      }
    }
  };

  const handleSubjectClick = (board, qual, subject) => {
    navigate(
      `/subjects?boardGuid=${board.guid}` +
        `&boardName=${encodeURIComponent(board.name || board.code)}` +
        `&qualificationGuid=${qual.guid}` +
        `&qualificationCode=${encodeURIComponent(
          qual.code || qual.name
        )}` +
        `&subjectGuid=${subject.guid}` +
        `&subjectCode=${encodeURIComponent(
          subject.code || subject.name
        )}`
    );
  };

  return (
    <Flex position="relative" ref={sidebarRef} h="100%" w="100%">
      {/* Collapse toggle — sits on the divider at the top right */}
      <Tooltip label={collapsed ? "Expand" : "Collapse"} placement="right" hasArrow>
        <Button
          onClick={onToggle}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          position="absolute"
          top="14px"
          right="-14px"
          zIndex={30}
          minW="28px"
          w="28px"
          h="28px"
          p={0}
          borderRadius="full"
          bg={a.surface}
          border="1px solid" borderColor={a.border}
          boxShadow="0 2px 6px rgba(0,0,0,0.08)"
          _hover={{ bg: a.hover }}
        >
          <Box
            as={ChevronDoubleLeftIcon}
            w="16px"
            h="16px"
            color={a.text2}
            transform={collapsed ? "rotate(180deg)" : "none"}
            transition="transform .2s ease"
          />
        </Button>
      </Tooltip>
      <VStack
        align="stretch"
        spacing="14px"
        w="100%"
        h="100%"
        px={collapsed ? "10px" : "16px"}
        pt="28px"
        pb="20px"
        bg={a.chrome}
        overflowY="auto"
      >
        {!collapsed && (
          <Text color={a.text2} fontSize="14px" fontWeight="400" pr="24px">
            Overview
          </Text>
        )}

        {collapsed ? (
          <Tooltip label="Home" placement="right" hasArrow>
            <Button
              onClick={() => navigate("/home")}
              variant="ghost"
              w="full"
              justifyContent="center"
              px={0}
              py="8px"
              borderRadius="12px"
              _hover={{ bg: a.hover }}
              aria-label="Home"
            >
              <Image className="app-nav-icon" src={HomeStroke} alt="Home" w="28px" h="28px" />
            </Button>
          </Tooltip>
        ) : (
        <Box>
          <Button
            aria-expanded={homeOpen}
            onClick={() => {
              setHomeOpen((v) => {
                if (v) {
                  setPanelBoardGuid(null);
                  setOpenQualGuid(null);
                }
                return !v;
              });
            }}
            w="182px"
            p="10px 15px"
            gap="58px"
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            bg={a.surface}
            borderRadius="14px"
            _hover={{ bg: a.hover }}
            _focusVisible={{ boxShadow: "0 0 0 3px rgba(0,0,0,0.2)" }}
          >
            <HStack
              spacing="8px"
              onClick={(e) => {
                e.stopPropagation();
                navigate("/home");
              }}
            >
              <Image className="app-nav-icon" src={HomeStroke} alt="" w="20px" h="20px" />
              <Text color={a.text2} fontSize="12px" fontWeight="300">
                Home
              </Text>
            </HStack>
            <Image
              src={homeOpen ? ButtonUpChevron : ButtonDownChevron}
              alt=""
              w="20px"
              h="20px"
            />
          </Button>

          {homeOpen && (
            <Box position="relative" pl="22px" mt="10px">
              <Box
                position="absolute"
                left="8px"
                top="4px"
                bottom="4px"
                w="1px"
                bg={a.border}
                transform="scaleX(0.2)"
                transformOrigin="left"
                borderRadius="1px"
              />
              <VStack align="stretch" spacing="2px">
                {loadingBoards && (
                  <Spinner size="sm" color={a.accent} ml="12px" />
                )}
                {!loadingBoards &&
                  boards.map((b) => (
                    <HomeChildRow
                      key={b.guid}
                      label={formatExamBoard(b.abbreviation || b.name || b.code)}
                      isActive={panelBoardGuid === b.guid}
                      onClick={(e) => handleBoardClick(b.guid, e)}
                    />
                  ))}
              </VStack>
            </Box>
          )}
        </Box>
        )}

        {!collapsed && (
          <Text color={a.text2} fontSize="14px" fontWeight="400">
            Workspace
          </Text>
        )}

        <VStack align="stretch" spacing="8px">
          <IconRow
            collapsed={collapsed}
            label="Spaces"
            lightSrc={ClassroomsIcon}
            darkSrc={ClassroomsIcon}
            onClick={() => {
              setCurrent("Spaces");
              navigate("/classrooms");
            }}
            isCurrent={location.pathname === "/classrooms"}
          />
          <IconRow
            collapsed={collapsed}
            label="Dashboard"
            lightSrc={DashboardIcon}
            darkSrc={DashboardIconDark}
            onClick={() => {
              setCurrent("Dashboard");
              navigate("/dashboard");
            }}
            isCurrent={current === "Dashboard"}
          />
          <IconRow
            collapsed={collapsed}
            label="My Papers"
            lightSrc={MyPapersIcon}
            darkSrc={MyPapersIconDark}
            onClick={() => {
              setCurrent("My papers");
              navigate("/my-papers");
            }}
            isCurrent={current === "My papers"}
          />
          <IconRow
            collapsed={collapsed}
            label="My Subjects"
            lightSrc={MySubjectsIcon}
            darkSrc={MySubjectsIconDark}
            onClick={() => {
              setCurrent("My subjects");
              navigate("/my-subjects");
            }}
            isCurrent={current === "My subjects"}
          />
        </VStack>

        <Box mt="auto" pt="24px">
          <VStack align="stretch" spacing="8px" mt="8px">
            <IconRow
              collapsed={collapsed}
              label="Settings"
              lightSrc={SettingsIcon}
              darkSrc={SettingsIconDark}
              onClick={() => {
                setCurrent("Settings");
                navigate("/settings");
              }}
              isCurrent={current === "Settings"}
            />
            <IconRow
              collapsed={collapsed}
              label="Logout"
              lightSrc={LogoutIcon}
              darkSrc={LogoutIconDark}
              onClick={() => {
                logout({ clearDefaults: false, navigate });
                navigate("/login");
              }}
              isCurrent={current === "Logout"}
            />
          </VStack>
        </Box>
      </VStack>

      {homeOpen && selectedBoard && (
        <Box
          ref={overlayRef}
          position="absolute"
          left={sidebarW}
          top={`${overlayTop}px`}
          display="inline-flex"
          padding="35px 46px 47px 13px"
          flexDirection="column"
          alignItems="flex-start"
          borderRadius="0 20px 20px 0"
          border="0.5px solid" borderColor={a.border} 
          borderLeft = "none"
          background={a.chrome}
          boxShadow="0 10px 20px 0 rgba(0, 0, 0, 0.00)"
          zIndex={999}
        >
          <Box minWidth ="489px">
            {loadingQualsForBoard[selectedBoard.guid] && (
              <Flex
                height="48px"
                alignItems="center"
                justifyContent="center"
                borderBottom="1px solid" borderBottomColor={a.border}
              >
                <Spinner size="sm" color={a.accent} />
              </Flex>
            )}

            {(boardQuals[selectedBoard.guid] || []).map((qual) => {
              const isOpen = openQualGuid === qual.guid;
              const subjects = subjectsByQual[qual.guid] || [];
              const qualLoading = loadingSubjects[qual.guid];

              return (
                <Box key={qual.guid} >
                  <Flex
                    height="48px"
                    padding="17px 226px 14px 12px"
                    alignItems="left"
                    borderBottom="1px solid" borderBottomColor={a.border}
                    cursor="pointer"
                    onClick={() => handleQualToggle(qual)}
                    _hover={{ opacity: 0.9 }}
                  >
                    <Text
                      color={a.text2}
                      textAlign="lseft"
                      fontSize="14px"
                      fontWeight="500"
                      flex="1"
                    >
                      {qual.code || qual.name}
                    </Text>
                  </Flex>

                  {isOpen && (
                    <Box py="8px" px="12px">
                      {qualLoading && (
                        <Spinner size="xs" color={a.accent} />
                      )}

                      {!qualLoading && subjects.length === 0 && (
                        <Text
                          color={a.text2}
                          textAlign="center"
                          fontSize="11px"
                          fontWeight="400"
                        >
                          No subjects found.
                        </Text>
                      )}

                      {!qualLoading && subjects.length > 0 && (
                        <Grid
                          templateColumns="repeat(2, minmax(0, 1fr))"
                          gap="8px"
                          columnGap="184px" 
                        >
                          {subjects.map((s) => (
                            <Box
                              key={s.guid}
                              display="flex"
                              alignItems="left"
                              justifyContent="left"
                              height="22px"
                              cursor="pointer"
                              whiteSpace="nowrap" 
                              _hover={{ opacity: 0.8 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSubjectClick(
                                  selectedBoard,
                                  qual,
                                  s
                                );
                              }}
                            >
                              <Text
                                color={a.text2}
                                textAlign="center"
                                fontSize="11px"
                                fontWeight="400" 
                                marginBottom = "8px"
                              >
                                {s.name || s.name}
                              </Text>
                            </Box>
                          ))}
                        </Grid>
                      )}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      )} 

    </Flex>
  );
};

export default Sidebar;
